import { buildCategoryChildrenTree, normalizePage, normalizeProduct } from '../normalizers'
import {
  buildCanonicalProductPath,
  buildCategoryPathFromTrail,
  resolveCategoryFromPath,
} from '../routing'
import {
  buildCanonicalPathFromCmsSlug,
  buildDefaultSeoAlternates,
  buildLocaleFallbackSignal,
  shouldFallbackToDefaultLocale,
} from '../seo/localePolicy'
import { resolveHomepageSlugFromRuntimeSettings } from '../runtimeSettings'
import { fetchJson } from '../ssr'
import type { Route } from '../types'
import type { SeoRouteLoadContext } from './types'
import { DEFAULT_SHOP_LOCALE, type ShopLocale } from '../../i18n/shopLocale'
import {
  enrichPageSeoLocaleData,
  fetchNormalizedCategories,
  fetchPageForLocale,
} from './loadersShared'

export const loadHomeSeoContext = async ({
  apiBase,
  locale,
  preloadedSettingsData,
  context,
}: {
  apiBase: string
  locale: ShopLocale
  preloadedSettingsData?: Record<string, unknown>
  context: SeoRouteLoadContext
}) => {
  const settingsData = preloadedSettingsData ?? (await fetchJson(`${apiBase}/settings/public`)).data
  const normalizedHomeSlug = resolveHomepageSlugFromRuntimeSettings(settingsData?.settings, '/')
  const pageResponse = await fetchPageForLocale({
    apiBase,
    pageIdOrSlug: normalizedHomeSlug,
    locale,
  })

  let resolvedLocale = locale
  let resolvedPageResponse = pageResponse
  if (
    !pageResponse.ok &&
    shouldFallbackToDefaultLocale({ requestedLocale: locale, responseStatus: pageResponse.status })
  ) {
    resolvedPageResponse = await fetchPageForLocale({
      apiBase,
      pageIdOrSlug: normalizedHomeSlug,
      locale: DEFAULT_SHOP_LOCALE,
    })
    if (resolvedPageResponse.ok) resolvedLocale = DEFAULT_SHOP_LOCALE
  }
  if (!resolvedPageResponse.ok) return

  const page = normalizePage((resolvedPageResponse.data?.page ?? {}) as Record<string, unknown>)
  page.seoCanonicalLocale = resolvedLocale
  if (resolvedLocale !== locale) {
    page.seoFallbackFromLocale = locale
  }
  await enrichPageSeoLocaleData({
    apiBase,
    page,
    fallbackPathResolver: () => '/',
  })
  page.seoAlternates =
    page.seoAlternates?.map((entry) => ({ ...entry, path: '/' })) ?? buildDefaultSeoAlternates('/')
  context.homePageData = page
  context.canonicalPath = '/'
}

export const loadPageSeoContext = async ({
  route,
  apiBase,
  locale,
  context,
}: {
  route: Extract<Route, { mode: 'page' }>
  apiBase: string
  locale: ShopLocale
  context: SeoRouteLoadContext
}) => {
  const pageResponse = await fetchPageForLocale({
    apiBase,
    pageIdOrSlug: route.slug,
    locale,
  })

  let resolvedLocale = locale
  let resolvedPageResponse = pageResponse
  if (
    !pageResponse.ok &&
    shouldFallbackToDefaultLocale({ requestedLocale: locale, responseStatus: pageResponse.status })
  ) {
    resolvedPageResponse = await fetchPageForLocale({
      apiBase,
      pageIdOrSlug: route.slug,
      locale: DEFAULT_SHOP_LOCALE,
    })
    if (resolvedPageResponse.ok) resolvedLocale = DEFAULT_SHOP_LOCALE
  }

  if (resolvedPageResponse.ok) {
    const page = normalizePage((resolvedPageResponse.data?.page ?? {}) as Record<string, unknown>)
    page.seoCanonicalLocale = resolvedLocale
    if (resolvedLocale !== locale) {
      page.seoFallbackFromLocale = locale
    }
    await enrichPageSeoLocaleData({ apiBase, page })
    const localeFallback = buildLocaleFallbackSignal({
      requestedLocale: locale,
      resolvedLocale,
    })
    if (localeFallback) {
      page.seoCanonicalLocale = localeFallback.resolvedLocale
      page.seoFallbackFromLocale = localeFallback.requestedLocale
    }
    context.pageData = page
    context.canonicalPath = buildCanonicalPathFromCmsSlug(context.pageData.slug)
    return
  }

  if (resolvedPageResponse.status !== 404) return

  const categories = await fetchNormalizedCategories(apiBase)
  const categoryMatch = resolveCategoryFromPath(categories, route.slug)
  if (categoryMatch) {
    const children = buildCategoryChildrenTree(categories, categoryMatch.category._id)
    context.categoryRouteData = {
      category: categoryMatch.category,
      trail: categoryMatch.trail,
      children,
      products: [],
    }
    context.canonicalPath = buildCategoryPathFromTrail(categoryMatch.trail)
    return
  }

  const productResponse = await fetchJson(`${apiBase}/catalog/products/${encodeURIComponent(route.slug)}`)
  if (!productResponse.ok) return
  context.productData = normalizeProduct((productResponse.data?.product ?? {}) as Record<string, unknown>)
  context.canonicalPath = buildCanonicalProductPath(context.productData, categories)
}
